import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Award, Mic, Users, Briefcase, Check, Sparkles, Clock, Calendar, Star, Brain, PlayCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookDemoDialog } from "@/components/BookDemoDialog";
import { PayCourseDialog } from "@/components/PayCourseDialog";
import { EnrollCourseButton } from "@/components/EnrollCourseButton";
import { FoundingProfileCard } from "@/components/FoundingProfileCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const courses = [
  {
    id: "ielts", courseId: "ielts", Icon: BookOpen, name: "IELTS Mastery", tag: "Most popular",
    price: "₹14,999", priceINR: 14999, duration: "4 weeks", batch: "Mon · Wed · Fri",
    color: "#D90429",
    features: ["8 full-length mocks", "Speaking with certified examiners", "Writing evaluation in 24 hrs", "Band 7+ or free re-enroll"],
  },
  {
    id: "pte", courseId: "pte", Icon: Award, name: "PTE Academic", tag: "Fast-track",
    price: "₹12,499", priceINR: 12499, duration: "4 weeks", batch: "Tue · Thu · Sat",
    color: "#14213D",
    features: ["AI-scored mocks (Pearson pattern)", "Personalised score analytics", "Repeat-sentence coaching", "Score 79+ blueprint"],
  },
  {
    id: "spoken", courseId: "spoken", Icon: Mic, name: "Spoken English", tag: "Bestseller",
    price: "₹8,999", priceINR: 8999, duration: "4 weeks", batch: "Daily · 1 hr",
    color: "#FFC300",
    features: ["Group + 1-on-1 speaking pods", "Neutral accent training", "Live roleplays every week", "WhatsApp practice group"],
  },
  {
    id: "pd", courseId: "pd", Icon: Users, name: "Personality Development", tag: "New",
    price: "₹7,499", priceINR: 7499, duration: "5 weeks", batch: "Weekends",
    color: "#D90429",
    features: ["Body language workshops", "Public speaking sessions", "Grooming & etiquette", "Confidence coaching"],
  },
  {
    id: "interview", courseId: "interview", Icon: Briefcase, name: "Interview Training", tag: "Visa · HR · Academic",
    price: "₹9,999", priceINR: 9999, duration: "2 weeks", batch: "Flexible slots",
    color: "#14213D",
    features: ["Visa officer style mocks", "HR & behavioural drills", "SOP & profile review", "Recorded video feedback"],
  },
  {
    id: "nlp", Icon: Brain, name: "Neuro Linguistic Programming", tag: "Self-Paced",
    price: "₹6,999", priceINR: 6999, duration: "5 modules", batch: "Lifetime access",
    color: "#D90429", courseId: "nlp_course", enrollFlow: true,
    features: [
      "Intro to NLP & the mind-language loop",
      "Rapport building (verbal + non-verbal)",
      "Anchoring & state management",
      "Reframing techniques for confidence",
      "NLP for interviews & public speaking",
    ],
  },
];

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function CoachingDashboard() {
  const { user } = useAuth();
  const [dbCourses, setDbCourses] = useState([]);

  const enrolledIds = useMemo(
    () => new Set((user?.enrolled_courses || []).map((e) => e.course_id)),
    [user]
  );
  const hasPurchased = (courseId) => enrolledIds.has(courseId);

  useEffect(() => {
    api.get("/courses").then((r) => setDbCourses(r.data || [])).catch(() => {});
  }, []);

  const courseList = useMemo(() => {
    const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
    const parseFeatures = (desc) => {
      if (!desc || typeof desc !== "string") return null;
      const lines = desc
        .split(/\r?\n/)
        .map((l) => l.replace(/^\s*[-•*·>]\s+/, "").trim())
        .filter(Boolean);
      return lines.length ? lines : null;
    };
    const staticIds = courses.map((c) => c.courseId || c.id);
    const merged = courses.map((c) => {
      const d = dbCourses.find((x) => x.course_id === (c.courseId || c.id));
      if (!d) return c;
      const parsed = parseFeatures(d.description);
      return {
        ...c, name: d.title, tag: d.tag || c.tag,
        price: fmt(d.price_inr), priceINR: d.price_inr,
        duration: d.duration || c.duration, batch: d.batch || c.batch,
        features: parsed || c.features,
      };
    });
    const extras = dbCourses
      .filter((x) => !staticIds.includes(x.course_id))
      .map((d) => ({
        id: d.course_id, Icon: BookOpen, name: d.title, tag: d.tag || "New",
        price: fmt(d.price_inr), priceINR: d.price_inr,
        duration: d.duration || "Flexible", batch: d.batch || "Contact us",
        color: "#D90429", features: parseFeatures(d.description) || [],
      }));
    return [...merged, ...extras];
  }, [dbCourses]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand-red/15 blur-3xl animate-blob" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/20 blur-3xl animate-blob" style={{ animationDelay: "5s" }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[minmax(0,1fr)_auto] gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red text-white text-xs font-semibold uppercase tracking-widest brand-shadow">
              <Sparkles className="w-3.5 h-3.5" /> Coaching Dashboard
            </motion.div>
            <motion.h1 variants={fadeUp} className="mt-6 font-heading font-black text-5xl md:text-6xl text-brand-navy leading-[1.05]">
              Your training roadmap, <span className="text-brand-red">custom-built.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 text-lg text-brand-navy/70 max-w-2xl">
              Choose a course, meet your mentor, and start crushing mocks from Day 1. Every batch is capped at
              12 students so you never fade into the background.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
              <BookDemoDialog
                defaultCourse="IELTS"
                leadType="coaching"
                trigger={
                  <Button data-testid="coaching-hero-demo-btn" className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold px-7 h-12 brand-shadow btn-scale">
                    Book a Free Demo
                  </Button>
                }
              />
              <a href="#courses">
                <Button variant="outline" className="rounded-full border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 h-12 btn-scale">
                  See all courses
                </Button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:flex justify-end"
          >
            <FoundingProfileCard
              theme="light"
              testId="coaching-founder-card"
              name="Pujali Garg"
              shortTitle="Co-Founder, Language Nest"
              photo="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/yyuf9oxs_image.png"
              titles={[
                "Co-Founder, Language Nest",
                "Director, Language Nest Foundation",
                "International Corporate Trainer",
                "NLP Coach",
                "Leadership & Human Potential Strategist",
              ]}
              bio={"Pujali Garg is a visionary educator, transformational trainer, and leadership mentor committed to empowering individuals and organizations through communication excellence, mindset transformation, and lifelong learning.\n\nWith 13+ years of professional experience, she has successfully coached students, corporate professionals, entrepreneurs, educators, and leaders to unlock their potential, enhance performance, and build meaningful careers.\n\nAs the Co-Founder of Language Nest and Director of Language Nest Foundation, she has played a pivotal role in creating one of India's emerging platforms for communication excellence, global education, and leadership development. Her mission is simple yet powerful: to transform lives by building confidence, developing future-ready skills, and creating leaders who inspire positive change.\n\nHer multidisciplinary academic background includes an MBA in Human Resource Management, a Bachelor's Degree in Zoology, TEFL Certification, and international learning from McMaster University, Canada, enabling her to blend global best practices with practical, real-world training methodologies."}
              expertise={[
                "NLP",
                "Corporate Training",
                "Leadership Coaching",
                "Emotional Intelligence",
                "Communication Excellence",
                "Mindset Transformation",
                "Public Speaking",
                "HR & People Development",
                "TEFL Certified",
              ]}
              quote={"Transformation begins with a single belief—that you are capable of becoming more than you are today. When mindset changes, communication improves. When communication improves, opportunities multiply. And when opportunities meet purpose, extraordinary success becomes inevitable."}
            />
          </motion.div>
        </div>
      </section>

      {/* Coaching-founder card on mobile — shown below hero */}
      <section className="lg:hidden -mt-10 mb-8 px-6">
        <div className="max-w-md mx-auto">
          <FoundingProfileCard
            theme="light"
            testId="coaching-founder-card-mobile"
            name="Pujali Garg"
            shortTitle="Co-Founder, Language Nest"
            photo="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/yyuf9oxs_image.png"
            titles={[
              "Co-Founder, Language Nest",
              "Director, Language Nest Foundation",
              "International Corporate Trainer",
              "NLP Coach",
              "Leadership & Human Potential Strategist",
            ]}
            bio={"Pujali Garg is a visionary educator, transformational trainer, and leadership mentor committed to empowering individuals and organizations through communication excellence, mindset transformation, and lifelong learning.\n\nWith 13+ years of professional experience, she has successfully coached students, corporate professionals, entrepreneurs, educators, and leaders to unlock their potential.\n\nAs the Co-Founder of Language Nest and Director of Language Nest Foundation, she has played a pivotal role in creating one of India's emerging platforms for communication excellence, global education, and leadership development.\n\nHer multidisciplinary academic background includes an MBA in Human Resource Management, a Bachelor's Degree in Zoology, TEFL Certification, and international learning from McMaster University, Canada."}
            expertise={[
              "NLP",
              "Corporate Training",
              "Leadership Coaching",
              "Emotional Intelligence",
              "Communication Excellence",
              "Public Speaking",
              "TEFL Certified",
            ]}
            quote={"Transformation begins with a single belief—that you are capable of becoming more than you are today."}
          />
        </div>
      </section>

      {/* Courses grid */}
      <section id="courses" className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseList.map((c, i) => (
              <motion.div
                key={c.id}
                variants={fadeUp}
                data-testid={`course-card-${c.id}`}
                className="card-hover rounded-3xl bg-white border border-brand-navy/10 p-8 relative overflow-hidden flex flex-col"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-10" style={{ background: c.color }} />
                <div className="relative flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-14 h-14 rounded-2xl grid place-items-center"
                      style={{ background: c.color === "#FFC300" ? "#FFC30022" : c.color + "18", color: c.color }}
                    >
                      <c.Icon className="w-7 h-7" />
                    </div>
                    <Badge className="bg-brand-gold text-brand-navy hover:bg-brand-gold-hover font-bold rounded-full px-3">{c.tag}</Badge>
                  </div>

                  <h3 className="font-heading font-black text-2xl text-brand-navy">{c.name}</h3>

                  <div className="mt-4 flex items-end gap-2">
                    <div className="font-heading font-black text-4xl text-brand-red">{c.price}</div>
                    <div className="text-sm text-brand-navy/50 mb-1.5">/ course</div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-brand-navy/60">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{c.duration}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{c.batch}</span>
                  </div>

                  <ul className="mt-6 space-y-2.5">
                    {c.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-brand-navy/80">
                        <Check className="w-4 h-4 mt-0.5 text-brand-red shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-7 flex flex-col gap-2.5">
                    <BookDemoDialog
                      defaultCourse={c.name}
                      leadType="coaching"
                      trigger={
                        <Button
                          data-testid={`course-demo-btn-${c.id}`}
                          className="w-full h-12 rounded-full bg-brand-navy hover:bg-brand-red text-white font-semibold btn-scale"
                        >
                          {c.enrollFlow ? "Try a free preview" : "Book Free Demo"}
                        </Button>
                      }
                    />
                    {hasPurchased(c.courseId) ? (
                      <Link to={`/courses/${c.courseId}`} className="block">
                        <Button
                          data-testid={`course-start-btn-${c.id}`}
                          className="w-full h-11 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale inline-flex items-center justify-center gap-2"
                        >
                          <PlayCircle className="w-4 h-4" />
                          Start Course
                        </Button>
                      </Link>
                    ) : c.enrollFlow ? (
                      <EnrollCourseButton
                        courseId={c.courseId}
                        courseName={c.name}
                        priceINR={c.priceINR}
                        label="Enroll & Pay"
                        data-testid={`course-enroll-btn-${c.id}`}
                        className="w-full h-11 rounded-full border-2 border-brand-red bg-white text-brand-red hover:bg-brand-red hover:text-white font-semibold btn-scale"
                      />
                    ) : (
                      <PayCourseDialog
                        course={c.name}
                        courseId={c.courseId}
                        priceINR={c.priceINR}
                        trigger={
                          <Button
                            data-testid={`course-enroll-btn-${c.id}`}
                            variant="outline"
                            className="w-full h-11 rounded-full border-2 border-brand-red bg-white text-brand-red hover:bg-brand-red hover:text-white font-semibold btn-scale"
                          >
                            Enroll & Pay
                          </Button>
                        }
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-20 bg-brand-muted-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-4 gap-6">
          {[
            { n: "5,000+", l: "Students trained" },
            { n: "92%", l: "Score target hit rate" },
            { n: "12", l: "Max batch size" },
            { n: "4.9★", l: "Google rating" },
          ].map((s) => (
            <div key={s.l} className="rounded-3xl bg-white p-8 border border-brand-navy/10 text-center card-hover">
              <div className="font-heading font-black text-4xl text-brand-red">{s.n}</div>
              <div className="mt-2 text-brand-navy/60 text-sm">{s.l}</div>
              <div className="flex gap-0.5 justify-center mt-3 text-brand-gold">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
